/**
 * A ticket drafted from Dialpad's call recap (spec `docs/superpowers/specs/2026-11-01-after-call-draft-design.md`).
 * Pure: Dialpad's summary, purposes, outcome and action items in; a subject, notes and whether
 * Dialpad called it resolved out. The rep sees all of it in quick capture and confirms; nothing
 * here writes. Card, SSN and bank numbers are replaced before the text reaches the form.
 */
import { suggestCategory } from './category-suggest.js';
import { redactForModel } from './redact.js';
/**
 * Dialpad's call purposes that mean one of our categories on their own. The rest ("Technical
 * Support" is 51 of 137 calls; "Operations", "Callback", "Other") say nothing about which, so the
 * words of the recap decide or nothing does.
 */
export const PURPOSE_CATEGORY = {
    Refund: 'Refund / Void',
    'Account Management': 'Account Changes',
    Compliance: 'Pricing, Fees & Rates',
    'Loyalty & Rewards': 'Inventory & Catalog',
    'Order Status': 'Shipping & RMA',
    'Return & Exchange': 'Shipping & RMA',
    'Service & Maintenance': 'Hardware & Peripherals',
    'Billing Questions': 'Funding & Deposits',
};
/** Subject first (precise), then the recap's first sentence (the topic), then the purpose. Never the whole recap: "further communication" read as a comm error. */
export function recapCategory(subject, summary, purposes) {
    const fromSubject = suggestCategory(subject);
    if (fromSubject)
        return fromSubject;
    const fromTopic = summary ? suggestCategory(firstSentence(summary)) : null;
    if (fromTopic)
        return fromTopic;
    for (const p of purposes ?? []) {
        const c = PURPOSE_CATEGORY[p.trim()];
        if (c)
            return c;
    }
    return null;
}
export const DRAFT_SUBJECT_MAX = 80;
export const DRAFT_NOTES_MAX = 5000;
export const DRAFT_FOOTER = "From Dialpad's call recap.";
export const DRAFT_RESOLUTION = 'Resolved on the call (Dialpad recap).';
/** Dialpad's stand-in for a call too short (or a voicemail) to summarize. */
export function isPlaceholderRecap(summary) {
    if (!summary)
        return true;
    return /^summaries are (currently )?not generated/i.test(summary.trim());
}
/** Dialpad sends action items as a list of strings; anything else is treated as none. */
export function actionItemsOf(value) {
    if (!Array.isArray(value))
        return [];
    return value.filter((v) => typeof v === 'string').map((v) => v.trim()).filter((v) => v.length > 0);
}
const ART = String.raw `(?:an? |the |their |some )?`;
const re = (source) => new RegExp(source, 'i');
/**
 * Dialpad's openings, most specific first; the first that reads wins. Read over 34 recaps from the
 * support line (Oct 2026): "X assists Y with Z", "in resolving Z", "is facing Z", "reports Z",
 * "to inquire about Z", "to request Z" (the verb is kept: it is what the rep acts on), "discuss Z".
 */
const TOPIC = [
    re(String.raw `\bassists?\b.*?\bin (?:resolving|fixing|troubleshooting|setting up|addressing|handling|investigating) ${ART}(?:issues? (?:related to|with) )?(.+)`),
    re(String.raw `\b(?:assist(?:s|ing|ed)?|help(?:s|ing|ed)?)\b.*?\bwith ${ART}(.+)`),
    re(String.raw `\b(?:faces|facing|experiencing|reports|reporting|having trouble with|is having) ${ART}(.+)`),
    re(String.raw `\bto (?:inquire|ask) about ${ART}(.+)`),
    re(String.raw `\bto ((?:request|change|add|update|cancel|set up) .+)`),
    re(String.raw `\bto check on ${ART}(.+)`),
    re(String.raw `\bdiscuss(?:es)? ${ART}(.+)`),
    re(String.raw `\b(?:regarding|about|concerning) ${ART}(.+)`),
];
/** Where the store, the place or the caller is named, or a new clause starts: not the topic. */
const TOPIC_END = /,| at (?:their|the|his|her|a|an)\b| at [A-Z'"]| in [A-Z][a-z]+,| for (?:their|the) store\b| (?:after|which|who|that|during|despite) | to [A-Z]|[.!?]/;
function firstSentence(text) {
    const m = /^(.+?[.!?])(\s|$)/.exec(text.trim());
    return (m?.[1] ?? text).trim();
}
function clip(text, max) {
    if (text.length <= max)
        return text;
    const cut = text.slice(0, max + 1);
    const space = cut.lastIndexOf(' ');
    return (space > max / 2 ? cut.slice(0, space) : text.slice(0, max)).replace(/[\s,;:]+$/, '');
}
/** Dialpad names the person it could not identify "External Speaker"; on the desk that is the caller. */
export function plainSpeakers(text) {
    return text.replace(/\b(an|the|An|The)\s+External Speakers?\b/g, (_m, a) => (a === 'An' || a === 'The' ? 'The caller' : 'the caller')).replace(/\bExternal Speakers?\b/g, 'the caller');
}
/** The topic of Dialpad's first sentence ("Micah assists Carlo with integrating a delivery app" gives "Integrating a delivery app"). */
export function recapTopic(summary) {
    const sentence = firstSentence(summary);
    for (const pattern of TOPIC) {
        const raw = pattern.exec(sentence)?.[1];
        if (!raw)
            continue;
        const topic = raw.split(TOPIC_END)[0]?.trim().replace(/\s+/g, ' ');
        if (!topic || topic.length < 6)
            continue;
        const words = clip(topic, DRAFT_SUBJECT_MAX);
        return words.charAt(0).toUpperCase() + words.slice(1);
    }
    return null;
}
export function callDraft(r) {
    const hasRecap = !isPlaceholderRecap(r.summary);
    const summary = hasRecap && r.summary ? plainSpeakers(redactForModel(r.summary.trim())) : null;
    const items = actionItemsOf(r.actionItems).map((i) => plainSpeakers(redactForModel(i)));
    const purpose = (r.purposes ?? []).map((p) => p.trim()).find((p) => p.length >= 3) ?? null;
    const subject = (summary ? recapTopic(summary) : null) ?? (purpose ? clip(purpose, DRAFT_SUBJECT_MAX) : null);
    let notes = null;
    if (summary || items.length > 0) {
        const parts = [];
        if (summary)
            parts.push(summary);
        if (items.length > 0)
            parts.push(['Next steps:', ...items.map((i) => `- ${i}`)].join('\n'));
        parts.push(DRAFT_FOOTER);
        notes = parts.join('\n\n');
        if (notes.length > DRAFT_NOTES_MAX)
            notes = `${notes.slice(0, DRAFT_NOTES_MAX - 1).trimEnd()}…`;
    }
    return { hasRecap, subject, notes, resolvedOnCall: /^\s*resolved\s*$/i.test(r.outcome ?? ''), category: recapCategory(subject, summary, r.purposes) };
}
//# sourceMappingURL=call-draft.js.map